import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { programs, type Program } from "@/mocks/programs";
import { radioStations } from "@/mocks/radios";
import { clips, formatearNumero, type Clip } from "@/mocks/clips";
import TvCarousel from "@/pages/tv-app/components/TvCarousel";

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

export default function TvProgramDetailPage() {
  const navigate = useNavigate();
  const { programSlug } = useParams<{ programSlug: string }>();
  const [program, setProgram] = useState<Program | null>(null);
  const [relatedClips, setRelatedClips] = useState<Clip[]>([]);
  const [focusedBtn, setFocusedBtn] = useState<string>("back");
  const [showQr, setShowQr] = useState(false);

  const buttons = ["back", "host", "radio", "qr"] as const;

  useEffect(() => {
    if (!programSlug) return;
    const found = programs.find((p) => p.slug === programSlug);
    if (!found) return;
    setProgram(found);
    const radio = radioStations.find((r) => r.id === found.radioId);
    const rFreq = radio?.frequency.replace(" FM", "").replace(" RFW", "") || "";
    setRelatedClips(clips.filter((c) => c.programaNombre === found.name || c.radioNombre === found.radioName).slice(0, 6));
  }, [programSlug]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        const idx = buttons.indexOf(focusedBtn as typeof buttons[number]);
        setFocusedBtn(buttons[(idx + 1) % buttons.length]);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const idx = buttons.indexOf(focusedBtn as typeof buttons[number]);
        setFocusedBtn(buttons[(idx - 1 + buttons.length) % buttons.length]);
      } else if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault();
        if (showQr) { setShowQr(false); return; }
        navigate("/tv-app");
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (focusedBtn === "back") navigate("/tv-app");
        else if (focusedBtn === "host" && program?.hosts[0]) navigate(`/tv-app/host/${program.hosts[0].id}`);
        else if (focusedBtn === "radio" && program) navigate(`/tv-app/radio/${program.radioId}`);
        else if (focusedBtn === "qr") setShowQr(true);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [focusedBtn, navigate, program, showQr]);

  if (!program) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center select-none">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-white/5 flex items-center justify-center mb-6"><i className="ri-mic-line text-white/40 text-3xl"></i></div>
          <h1 className="text-2xl font-bold text-white mb-3">Programa no encontrado</h1>
          <button onClick={() => navigate("/tv-app")} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold cursor-pointer transition-all outline-none focus:ring-2 focus:ring-white"><i className="ri-arrow-left-line mr-2"></i>Volver</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white select-none overflow-x-hidden">
      {/* Cover */}
      <div className="relative h-[350px] lg:h-[440px] overflow-hidden">
        <img src={program.coverUrl} alt="" className="w-full h-full object-cover opacity-35 blur-sm scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/50 to-[#0a0a0f]/30"></div>
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 lg:px-10 py-5 z-10">
          <button onClick={() => navigate("/tv-app")} className={`flex items-center gap-2 px-5 py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-semibold ${focusedBtn === "back" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30" : "bg-white/10 hover:bg-white/15 text-white"}`}>
            <i className="ri-arrow-left-line text-lg"></i>Volver
          </button>
          <div className="flex items-center gap-3">
            {program.isActive && (<span className="px-3 py-1.5 rounded-full bg-emerald-500/90 text-white text-xs font-bold flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>ACTIVO</span>)}
            <span className="px-3 py-1.5 rounded-full bg-white/10 text-white text-xs font-bold">{program.category}</span>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 z-10 px-6 lg:px-10 pb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-white">{program.name}</h1>
          <div className="flex items-center gap-4 mt-3 flex-wrap">
            <span className="text-lg text-white/60"><i className="ri-time-line mr-1.5"></i>{program.schedule}</span>
            <span className="text-lg text-white/60">{program.radioName} · {program.radioFrequency}</span>
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-10 py-8 space-y-8">
        {/* Hosts */}
        {program.hosts.length > 0 && (
          <div className="flex flex-wrap gap-4">
            {program.hosts.map((host) => (
              <button
                key={host.id}
                tabIndex={0}
                onClick={() => navigate(`/tv-app/host/${host.id}`)}
                className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all cursor-pointer outline-none ${focusedBtn === "host" ? "bg-white/20 ring-2 ring-white" : "bg-white/5 hover:bg-white/10"}`}
              >
                <img src={host.photoUrl} alt={host.name} className="w-12 h-12 rounded-full object-cover" />
                <div className="text-left">
                  <p className="text-base font-bold text-white">{host.name}</p>
                  <p className="text-sm text-white/50">{host.role}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Description */}
        <div className="max-w-3xl">
          <p className="text-base text-white/60 leading-relaxed">{program.description}</p>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-10 flex-wrap">
          <div><p className="text-3xl font-bold text-white">{formatCount(program.followers)}</p><p className="text-sm text-white/50 mt-1">Seguidores</p></div>
          <div className="w-px h-10 bg-white/10"></div>
          <div><p className="text-3xl font-bold text-accent-400">{formatCount(program.community.comments)}</p><p className="text-sm text-white/50 mt-1">Comentarios</p></div>
          <div className="w-px h-10 bg-white/10"></div>
          <div><p className="text-3xl font-bold text-accent-400">{formatCount(program.community.shares)}</p><p className="text-sm text-white/50 mt-1">Compartidos</p></div>
        </div>

        {/* Tags */}
        <div className="flex items-center gap-2 flex-wrap">
          {program.tags.map((tag) => (<span key={tag} className="px-3 py-1 rounded-full bg-white/5 text-white/50 text-xs font-semibold">{tag}</span>))}
        </div>

        {/* Related Clips */}
        {relatedClips.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold text-white mb-5 flex items-center gap-3"><div className="w-8 h-8 rounded-lg bg-accent-400/10 flex items-center justify-center"><i className="ri-film-line text-accent-400 text-sm"></i></div>Videos relacionados ({relatedClips.length})</h2>
            <TvCarousel>
              {relatedClips.map((clip) => (
                <div key={clip.id} tabIndex={0} onClick={() => navigate(`/tv-app/video/${clip.id}`)} data-tv-focusable="true" className="relative flex-shrink-0 w-[340px] aspect-[16/10] rounded-2xl overflow-hidden bg-background-100 group cursor-pointer transition-all outline-none focus:ring-[3px] focus:ring-accent-400 focus:ring-offset-4 focus:ring-offset-[#0a0a0f] hover:ring-[3px] hover:ring-accent-400/40 hover:ring-offset-4 hover:ring-offset-[#0a0a0f]">
                  <img src={clip.imagenUrl} alt={clip.titulo} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/10 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-5"><h3 className="text-lg font-bold text-white truncate">{clip.titulo}</h3><p className="text-sm text-white/60 mt-1 flex items-center gap-1"><i className="ri-eye-line text-xs"></i>{formatearNumero(clip.vistas)}</p></div>
                </div>
              ))}
            </TvCarousel>
          </section>
        )}

        {/* Bottom Controls */}
        <div className="flex items-center justify-center gap-3 pt-4 pb-8">
          <button onClick={() => navigate("/tv-app")} className={`flex items-center gap-2 px-5 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-bold ${focusedBtn === "back" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"}`}><i className="ri-arrow-left-line text-lg"></i>Volver</button>
          <button onClick={() => program && navigate(`/tv-app/radio/${program.radioId}`)} className={`flex items-center gap-2 px-5 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-bold ${focusedBtn === "radio" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"}`}><i className="ri-radio-line text-lg"></i>Ver emisora</button>
          <button onClick={() => setShowQr(true)} className={`flex items-center gap-2 px-5 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-bold ${focusedBtn === "qr" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"}`}><i className="ri-qr-code-line text-lg"></i>Abrir en el celu</button>
        </div>

        {/* QR */}
        {showQr && (
          <div className="fixed inset-0 z-50 bg-[#0a0a0f]/90 backdrop-blur-sm flex items-center justify-center" onClick={() => setShowQr(false)}>
            <div className="bg-[#0f0f15] border border-white/10 rounded-3xl p-8 max-w-md w-full mx-4 text-center" onClick={(e) => e.stopPropagation()}>
              <div className="w-48 h-48 mx-auto rounded-2xl bg-white p-3 mb-6"><img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`https://radiofrecuenciaweb.com/program/${program.slug}`)}`} alt="QR" className="w-full h-full object-contain rounded-xl" /></div>
              <h3 className="text-xl font-bold text-white mb-2">Ver en el celular</h3>
              <p className="text-sm text-white/50 mb-2">Escaneá el QR para seguir {program.name} en tu celular.</p>
              <p className="text-sm text-white/30 font-mono mb-6">radiofrecuenciaweb.com/program/{program.slug}</p>
              <button onClick={() => setShowQr(false)} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold cursor-pointer transition-all">Cerrar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}